/**
 * featurize.ts — RBF featurization for the fishing action.
 *
 * The raw action a = (x, y, r, g, b, size) ∈ [0,1]^6 is a poor input to a
 * linear bandit: catch probability is a smooth non-linear peak at the fish's
 * preferred action, and a linear model on raw features can only fit a tilted
 * plane through that peak (§3.1).
 *
 * Fix: expand a into radial-basis features, one per anchor. A linear model on
 * these features can now represent any smooth function of a — the standard
 * kernel-approximation trick (§3.2).
 *
 * ---
 *
 * Anchor design (v2). The original marginal-only layout (one independent 1-D
 * grid per feature) could not represent the *spatial* belief: an additive
 * `f(x) + f(y)` model can only form a cross shape, not a 2-D Gaussian bump,
 * so the agent's heat-map never concentrated on the flock even when it was
 * learning the colour/size axes correctly.
 *
 * We now use a **2-D grid for position** (anchors tile the lake plane, so a
 * linear model can place a bump anywhere) plus independent 1-D marginal grids
 * for the non-spatial features. Feature order:
 *
 *   block 0:  2-D position grid  GX × GY    (e.g. 8×8 = 64 anchors)
 *   block 1:  1-D R channel grid             (e.g. 6)
 *   block 2:  1-D G channel grid             (e.g. 6)
 *   block 3:  1-D B channel grid             (e.g. 6)
 *   block 4:  1-D size grid                  (e.g. 5)
 *
 * Total M = 64 + 6 + 6 + 6 + 5 = 87 at the defaults. Still trivial for the
 * Cholesky solver at 1 Hz.
 */

import { norm } from './linalg';

/** A continuous fishing action. All channels in [0, 1]. */
export interface Action {
  x: number;
  y: number;
  r: number;
  g: number;
  b: number;
  size: number;
}

export const ACTION_DIM = 6;

/** Default basis width σ² for spatial anchors. */
export const SPATIAL_SIGMA2 = 0.5 * 0.5 * 0.5 * 0.5 * 2; // ~0.125 → broad enough that anchors overlap
/** Default basis width σ² for the colour/size anchors. */
export const FEATURE_SIGMA2 = 0.05;

/** k evenly spaced anchors in [0, 1], inclusive of both ends. */
export function grid1d(k: number): number[] {
  if (k <= 1) return [0];
  const out = new Array<number>(k);
  for (let i = 0; i < k; i++) out[i] = i / (k - 1);
  return out;
}

/** Single RBF kernel evaluation exp(-(v - a)² / 2σ²). */
export function rbf(v: number, anchor: number, sigma2: number): number {
  return Math.exp(-((v - anchor) * (v - anchor)) / (2 * sigma2));
}

/**
 * Anchor layout. The spatial block uses a 2-D grid; the colour/size blocks
 * are 1-D marginals. The feature vector is the concatenation of all blocks.
 */
export interface AnchorLayout {
  /** Position anchors as flat [x0,y0, x0,y1, …] pairs. */
  posGrid: { x: number; y: number }[];
  /** Per-marginal-feature grids, in feature order (R, G, B, size). */
  marginals: number[][];
  /** Number of anchors in each block: [pos, R, G, B, size]. */
  counts: number[];
  /** Start index of each block in the feature vector. */
  offsets: number[];
  /** Total feature dimension M. */
  M: number;
  /** σ² for the spatial block. */
  spatialSigma2: number;
  /** σ² for the marginal blocks. */
  marginalSigma2: number;
}

/** Block indices used throughout the code. */
export const POS_BLOCK = 0;
export const R_BLOCK = 1;
export const G_BLOCK = 2;
export const B_BLOCK = 3;
export const SIZE_BLOCK = 4;
export const N_BLOCKS = 5;

/** Build the default layout (8×8 position grid + 6/6/6/5 marginal grids). */
export function defaultLayout(): AnchorLayout {
  return buildLayout(8, [6, 6, 6, 5]);
}

export function buildLayout(
  posBins: number,
  marginalBins: [number, number, number, number] | number[],
): AnchorLayout {
  const xs = grid1d(posBins);
  const ys = grid1d(posBins);
  const posGrid: { x: number; y: number }[] = [];
  for (const y of ys) for (const x of xs) posGrid.push({ x, y });
  const marginals = [
    grid1d(marginalBins[0]),
    grid1d(marginalBins[1]),
    grid1d(marginalBins[2]),
    grid1d(marginalBins[3]),
  ];
  const counts = [posGrid.length, ...marginals.map((m) => m.length)];
  const offsets = new Array<number>(counts.length);
  let acc = 0;
  for (let i = 0; i < counts.length; i++) {
    offsets[i] = acc;
    acc += counts[i];
  }
  return {
    posGrid,
    marginals,
    counts,
    offsets,
    M: acc,
    spatialSigma2: SPATIAL_SIGMA2,
    marginalSigma2: FEATURE_SIGMA2,
  };
}

/**
 * Expand an action into its RBF feature vector, L2-normalised. A reusable
 * `out` buffer may be passed for the hot path (candidate sampling, heat-map).
 *
 * Block 0 (position) uses a 2-D RBF per anchor: each spatial anchor fires
 * with exp(-‖(x,y) − anchor‖² / 2σ²_pos). Colour/size blocks use 1-D RBFs.
 */
export function featurize(a: Action, layout: AnchorLayout, out?: Float64Array): Float64Array {
  const { posGrid, marginals, offsets, M, spatialSigma2, marginalSigma2 } = layout;
  const buf = out && out.length === M ? out : new Float64Array(M);
  const s2 = spatialSigma2;
  const twoS2 = 2 * s2;
  for (let i = 0; i < posGrid.length; i++) {
    const ax = posGrid[i].x;
    const ay = posGrid[i].y;
    const ddx = a.x - ax;
    const ddy = a.y - ay;
    buf[offsets[POS_BLOCK] + i] = Math.exp(-(ddx * ddx + ddy * ddy) / twoS2);
  }
  const mvals = [a.r, a.g, a.b, a.size];
  for (let f = 0; f < marginals.length; f++) {
    const grid = marginals[f];
    const off = offsets[R_BLOCK + f];
    const v = mvals[f];
    for (let i = 0; i < grid.length; i++) {
      const d = v - grid[i];
      buf[off + i] = Math.exp(-(d * d) / (2 * marginalSigma2));
    }
  }
  // L2-normalise so ‖φ(a)‖ = 1.
  const n = norm(buf);
  if (n > 0) for (let i = 0; i < M; i++) buf[i] /= n;
  return buf;
}

/**
 * Build the feature vector for a *spatial* pixel (x, y) with the agent's
 * current best-guess colour and size plugged in (§6.1). Used to render the
 * heat-map at a grid of pixels.
 */
export function featurizePixel(
  x: number,
  y: number,
  color: { r: number; g: number; b: number },
  size: number,
  layout: AnchorLayout,
  out?: Float64Array,
): Float64Array {
  return featurize({ x, y, r: color.r, g: color.g, b: color.b, size }, layout, out);
}

/**
 * Slice a feature's weight block and return it with the matching anchor
 * values. The UI renders these as a belief bar. For the position block this
 * is the 2-D grid (rendered as the heat-map instead).
 */
export function marginalWeights(
  block: number,
  weights: Float64Array,
  layout: AnchorLayout,
): { anchors: number[]; weights: number[] } {
  const start = layout.offsets[block];
  const len = layout.counts[block];
  const grid = layout.marginals[block - R_BLOCK];
  const out = new Array<number>(len);
  for (let i = 0; i < len; i++) out[i] = weights[start + i];
  return { anchors: grid, weights: out };
}

/**
 * The spatial weight block as a 2-D array [gy][gx] for heat-map rendering or
 * direct inspection. Returns the raw weights (signed, not normalised).
 */
export function spatialWeights(
  weights: Float64Array,
  layout: AnchorLayout,
): { grid: number[][]; xAnchors: number[]; yAnchors: number[]; posBins: number } {
  const start = layout.offsets[POS_BLOCK];
  // Reconstruct the grid size: posGrid is row-major over (y, x) — see buildLayout.
  const n = layout.counts[POS_BLOCK];
  const posBins = Math.round(Math.sqrt(n));
  const xAnchors = grid1d(posBins);
  const yAnchors = grid1d(posBins);
  const grid: number[][] = [];
  for (let gy = 0; gy < posBins; gy++) {
    const row: number[] = [];
    for (let gx = 0; gx < posBins; gx++) {
      row.push(weights[start + gy * posBins + gx]);
    }
    grid.push(row);
  }
  return { grid, xAnchors, yAnchors, posBins };
}

/** argmax over a feature's marginal block. Returns the anchor value. */
export function topMarginalValue(block: number, weights: Float64Array, layout: AnchorLayout): number {
  const start = layout.offsets[block];
  const len = layout.counts[block];
  const grid = layout.marginals[block - R_BLOCK];
  let bi = 0;
  let bv = -Infinity;
  for (let i = 0; i < len; i++) {
    if (weights[start + i] > bv) {
      bv = weights[start + i];
      bi = i;
    }
  }
  return grid[bi];
}

/**
 * argmax over the 2-D spatial block. Returns the (x, y) the agent currently
 * believes is the best place to cast. Used for the crosshair overlay and the
 * refinement candidates.
 */
export function topSpatial(weights: Float64Array, layout: AnchorLayout): { x: number; y: number } {
  const start = layout.offsets[POS_BLOCK];
  const n = layout.counts[POS_BLOCK];
  const posBins = Math.round(Math.sqrt(n));
  let bi = 0;
  let bv = -Infinity;
  for (let i = 0; i < n; i++) {
    if (weights[start + i] > bv) {
      bv = weights[start + i];
      bi = i;
    }
  }
  const gx = bi % posBins;
  const gy = Math.floor(bi / posBins);
  const xs = grid1d(posBins);
  const ys = grid1d(posBins);
  return { x: xs[gx], y: ys[gy] };
}
